import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
  useParams,
} from "@tanstack/react-router";
import { lazy } from "react";
import { validateNotificationActionSearch } from "@/modules/notifications/public/notification-action-search";
import { ensureSyncBoundary } from "@/modules/sync/public/api";
import { ApiError, api, unwrap } from "@/shared/api/client";
import { qk } from "@/shared/api/keys";
import type { Session } from "@/shared/api/types";
import { getWorkspaceStartPath } from "@/shared/lib/navigation";
import {
  type RootPlacementSearch,
  validateRootPlacementSearch,
} from "@/shared/lib/rootPlacementSearch";
import { validateTaskListSearch } from "@/shared/lib/taskListSearch";
import { isNative } from "@/shared/platform/native";
import { phoneGateDecision, safeGateReturn } from "@/shared/platform/phoneGate";
import { NotFound } from "@/shared/ui/layout/NotFound";
import { RootLayout, RouteError } from "@/shared/ui/layout/RootLayout";

/**
 * Routing.
 *
 * Product URLs remain Turkish because they are part of the interface. A short,
 * explicit set of common bookmarks is redirected below; retired domain route
 * trees are intentionally absent so removed concepts cannot silently return.
 *
 * Everything below `/` requires a session; the guard lives on one parent route
 * rather than on every leaf, so a new screen is protected by default instead of
 * by remembering.
 */

type RouterContext = { queryClient: QueryClient };
type AuthReturnSearch = { next?: string };
type RecordSelectionSearch = { kayit?: string; ctx?: string };
type GlobalSearch = { q?: string; kinds?: string };
type TodaySearch = RecordSelectionSearch & {
  tarih?: string;
  gorunum?: "hafta" | "ay" | "gun" | "yil";
};
type AnalysisView = "genel" | "egilimler" | "sapmalar";
type AnalysisSearch = { gorunum?: AnalysisView; ctx?: string };
type NotesView = "notlar" | "taslaklar" | "yeniden-kesfet" | "gecmis";
type NotesSearch = RecordSelectionSearch & {
  gorunum?: NotesView;
  proje?: string;
} & RootPlacementSearch;
type KnowledgeNetworkSearch = {
  gorunum?: "bilgiler" | "haritalar";
  bilgi?: string;
  harita?: string;
  ctx?: string;
} & RootPlacementSearch;
type FocusSearch = { gorev?: string; proje?: string; ctx?: string };
type WorkSearch = RecordSelectionSearch & { proje?: string };
type ProjectListSearch = { kayit?: string; ctx?: string };
/** The Project workspace owns its areas in the URL so each one can be linked to. */
type ProjectSearch = {
  panel?: "ozet" | "plan" | "bilgi" | "harita" | "kayitlar";
  ctx?: string;
};
type RootCollectionSearch = RootPlacementSearch & { ctx?: string };

const validateAuthReturnSearch = (search: Record<string, unknown>): AuthReturnSearch => ({
  // The final destination is validated again by `resolveAuthReturn`: ordinary
  // app locations stay relative, while OAuth document navigation must match
  // the server's advertised authorization endpoint.
  next: typeof search.next === "string" ? search.next : undefined,
});

const validateRootCollectionSearch = (search: Record<string, unknown>): RootCollectionSearch => ({
  ...validateRootPlacementSearch(search),
  ctx: typeof search.ctx === "string" ? search.ctx : undefined,
});

const validateRecordSelectionSearch = (search: Record<string, unknown>): RecordSelectionSearch => ({
  kayit: typeof search.kayit === "string" ? search.kayit : undefined,
  ctx: typeof search.ctx === "string" ? search.ctx : undefined,
});

const validateGlobalSearch = (search: Record<string, unknown>): GlobalSearch => ({
  q: typeof search.q === "string" ? search.q.slice(0, 200) : undefined,
  kinds: typeof search.kinds === "string" ? search.kinds : undefined,
});

const validateTodaySearch = (search: Record<string, unknown>): TodaySearch => ({
  ...validateRecordSelectionSearch(search),
  tarih: typeof search.tarih === "string" ? search.tarih : undefined,
  gorunum:
    search.gorunum === "hafta" ||
    search.gorunum === "ay" ||
    search.gorunum === "gun" ||
    search.gorunum === "yil"
      ? search.gorunum
      : undefined,
});

const validateAnalysisSearch = (search: Record<string, unknown>): AnalysisSearch => ({
  ctx: typeof search.ctx === "string" ? search.ctx : undefined,
  gorunum:
    search.gorunum === "genel" || search.gorunum === "egilimler" || search.gorunum === "sapmalar"
      ? search.gorunum
      : undefined,
});

const validateKnowledgeNetworkSearch = (
  search: Record<string, unknown>,
): KnowledgeNetworkSearch => ({
  ...validateRootPlacementSearch(search),
  ctx: typeof search.ctx === "string" ? search.ctx : undefined,
  bilgi: typeof search.bilgi === "string" ? search.bilgi : undefined,
  harita: typeof search.harita === "string" ? search.harita : undefined,
  gorunum:
    search.gorunum === "bilgiler" || search.gorunum === "haritalar" ? search.gorunum : undefined,
});

const validateFocusSearch = (search: Record<string, unknown>): FocusSearch => ({
  gorev: typeof search.gorev === "string" ? search.gorev : undefined,
  proje: typeof search.proje === "string" ? search.proje : undefined,
  ctx: typeof search.ctx === "string" ? search.ctx : undefined,
});

const validateWorkSearch = (search: Record<string, unknown>): WorkSearch => ({
  ...validateRecordSelectionSearch(search),
  proje: typeof search.proje === "string" ? search.proje : undefined,
});

const validateProjectListSearch = (search: Record<string, unknown>): ProjectListSearch => ({
  kayit:
    typeof search.kayit === "string" && /^(goal|decision):[^:]+$/.test(search.kayit)
      ? search.kayit
      : undefined,
  ctx: typeof search.ctx === "string" ? search.ctx : undefined,
});

const validateProjectSearch = (search: Record<string, unknown>): ProjectSearch => ({
  ctx: typeof search.ctx === "string" ? search.ctx : undefined,
  panel:
    search.panel === "ozet" ||
    search.panel === "plan" ||
    search.panel === "bilgi" ||
    search.panel === "harita" ||
    search.panel === "kayitlar"
      ? search.panel
      : undefined,
});

const validateNotesSearch = (search: Record<string, unknown>): NotesSearch => ({
  ...validateRootPlacementSearch(search),
  ...validateRecordSelectionSearch(search),
  proje: typeof search.proje === "string" ? search.proje : undefined,
  gorunum:
    search.gorunum === "notlar" ||
    search.gorunum === "taslaklar" ||
    search.gorunum === "yeniden-kesfet" ||
    search.gorunum === "gecmis"
      ? search.gorunum
      : undefined,
});

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  notFoundComponent: NotFound,
  // Without this, a render that throws unmounts the tree and leaves a blank
  // page — no explanation, no way back.
  errorComponent: RouteError,
});

// The authenticated shell owns persistent windows, command surfaces and live
// watchers. Keep that signed-in graph out of the public boot path: importing it
// eagerly made Vite module-preload every feature shared by those surfaces even
// on the sign-in and password-reset routes.
const LazyAppShell = lazy(() =>
  import("@/app/shell/AppShell").then((module) => ({ default: module.AppShell })),
);

// --- Public -----------------------------------------------------------------

const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "auth",
  component: lazy(() =>
    import("@/modules/identity/public/auth-layout").then((m) => ({ default: m.AuthLayout })),
  ),
});

const loginRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/giris",
  validateSearch: validateAuthReturnSearch,
  component: lazy(() =>
    import("@/modules/identity/public/login-page").then((m) => ({ default: m.LoginPage })),
  ),
});

const registerRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/kayit",
  validateSearch: validateAuthReturnSearch,
  component: lazy(() =>
    import("@/modules/identity/public/register-page").then((m) => ({ default: m.RegisterPage })),
  ),
});

const resetRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/parola-sifirla",
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    token: typeof search.token === "string" ? search.token : undefined,
  }),
  component: lazy(() =>
    import("@/modules/identity/public/reset-password-page").then((m) => ({
      default: m.ResetPasswordPage,
    })),
  ),
});

// Password-reset emails emitted before the canonical Turkish route was wired
// used this public URL. Redirect it losslessly so old, still-valid two-hour
// links continue to work while every new email uses `/parola-sifirla`.
const legacyResetRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/reset-password",
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    token: typeof search.token === "string" ? search.token : undefined,
  }),
  beforeLoad: ({ search }) => {
    throw redirect({ to: "/parola-sifirla", search, replace: true });
  },
});

/**
 * The phone download screen.
 *
 * A sibling of the authenticated shell rather than a child of it: a phone
 * visitor with no session has to reach it, and pulling the signed-in module
 * graph onto a device that is about to be redirected away would be wasteful.
 */
const mobileGateRoute = createRoute({
  getParentRoute: () => rootRoute,
  // Spelled out rather than interpolated from `PHONE_GATE_PATH`: the route
  // quality matrix reads this file as text and only sees literal paths. A
  // router test asserts the two stay the same string.
  path: "/uygulama",
  validateSearch: (search: Record<string, unknown>): { next?: string } => ({
    next: safeGateReturn(typeof search.next === "string" ? search.next : undefined),
  }),
  component: lazy(() =>
    import("@/app/pages/MobileAppGatePage").then((module) => ({
      default: module.MobileAppGatePage,
    })),
  ),
});

// --- Signed in --------------------------------------------------------------

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    ctx?: string;
    lifeArea?: string;
    lifeTable?: string;
    lifeTab?: "records" | "metrics" | "rules";
  } => ({
    ctx: typeof search.ctx === "string" ? search.ctx : undefined,
    lifeArea: typeof search.lifeArea === "string" ? search.lifeArea : undefined,
    lifeTable: typeof search.lifeTable === "string" ? search.lifeTable : undefined,
    lifeTab:
      search.lifeTab === "records" || search.lifeTab === "metrics" || search.lifeTab === "rules"
        ? search.lifeTab
        : undefined,
  }),
  /**
   * The session guard.
   *
   * Resolved before any child renders, so a signed-out visitor never sees a
   * flash of an empty Today. `ensureQueryData` means the same request also
   * primes the cache the shell is about to read.
   */
  beforeLoad: async ({ context, location }) => {
    // The device gate runs before the session is even asked for. On a phone the
    // product is the native app: only authentication and account surfaces stay
    // open here, because the Android/iOS apps obtain their OAuth 2.1 + PKCE
    // authorization against this origin. Gating the route rather than the shell
    // is what makes a deep link, a bookmark and a share-target hand-off all
    // land on the download screen instead of a half-rendered workspace — and
    // the original location is carried along so the explicit browser escape
    // hatch can resume it.
    const gate = phoneGateDecision({ pathname: location.pathname, href: location.href });
    if (gate.gated) {
      throw redirect({
        to: gate.to,
        search: gate.next ? { next: gate.next } : {},
        replace: true,
      });
    }
    try {
      const session = await context.queryClient.ensureQueryData({
        queryKey: qk.session,
        queryFn: () => unwrap(api.GET("/api/v4/me", {})),
        retry: false,
      });
      if (
        (session as Session).preferences.has_completed_onboarding === false &&
        location.pathname !== "/baslangic"
      ) {
        throw redirect({
          to: "/baslangic",
          search: { next: location.href },
          replace: true,
        });
      }
      // The cross-domain cursor is a first-party browser contract. Native OAuth
      // clients refresh their scoped snapshots through SyncInvalidationWatcher.
      if (!isNative()) await ensureSyncBoundary(context.queryClient, (session as Session).user.id);
    } catch (error) {
      // Only 401 means "not signed in". A 500, a dropped connection or a
      // timeout used to land here too, and sending someone to the sign-in
      // screen for a server fault is both wrong and baffling: they sign in
      // successfully and never learn what happened. Anything else is rethrown
      // so the error boundary can say what it was.
      if (error instanceof ApiError && error.isUnauthorized) {
        throw redirect({ to: "/giris", search: { next: location.href } });
      }
      throw error;
    }
  },
  component: LazyAppShell,
});

const pagesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/sayfalar",
  component: lazy(() =>
    import("@/workspaces/pages/pages/PagesPage").then((module) => ({ default: module.PagesPage })),
  ),
});
const pageDocumentRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/sayfalar/$pageId",
  component: lazy(() =>
    import("@/workspaces/pages/pages/PagesPage").then((module) => ({ default: module.PagesPage })),
  ),
});
const pageEntityDocumentRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/sayfalar/nesne/$entityType/$entityId",
  component: lazy(() =>
    import("@/workspaces/pages/pages/PagesPage").then((module) => ({ default: module.PagesPage })),
  ),
});

/**
 * Where `/` goes.
 *
 * The account's own choice wins; Pages is the default for an account that has
 * not made one. Pages is behind a server capability, so when the destination
 * would be Pages the flag is resolved first — landing somebody on a workspace
 * the server has switched off would be worse than the old default. The request
 * is skipped entirely when the stored preference already points elsewhere.
 */
const indexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/",
  beforeLoad: async ({ context }) => {
    const session = context.queryClient.getQueryData<Session>(qk.session);
    const preference = session?.preferences.start_workspace;
    const wouldOpenPages = getWorkspaceStartPath(preference) === "/sayfalar";
    let pagesEnabled = true;
    if (wouldOpenPages) {
      try {
        const capabilities = await context.queryClient.ensureQueryData({
          queryKey: ["capabilities"],
          queryFn: () => unwrap(api.GET("/api/v4/meta/capabilities", {})),
        });
        pagesEnabled = Boolean((capabilities as { pages_enabled?: boolean }).pages_enabled);
      } catch {
        // An unreadable capability answer must not strand the visitor on `/`.
        pagesEnabled = false;
      }
    }
    throw redirect({
      to: getWorkspaceStartPath(preference, { pagesEnabled }),
      search: {},
    });
  },
});

const globalSearchRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/arama",
  validateSearch: validateGlobalSearch,
  component: lazy(() =>
    import("@/modules/notes/public/routes").then((module) => ({
      default: module.SearchResultsPage,
    })),
  ),
});

const savedViewsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/kayitli-gorunumler",
  component: lazy(() =>
    import("@/modules/saved-views").then((module) => ({ default: module.SavedViewsPage })),
  ),
});

// Keep only common, semantically unambiguous bookmark shortcuts.
const tasksShortcutRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/gorevler",
  beforeLoad: () => {
    throw redirect({ to: "/planlayici/gorevler" });
  },
});

const todayShortcutRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/bugun",
  validateSearch: validateTodaySearch,
  beforeLoad: ({ search }) => {
    throw redirect({ to: "/planlayici", search });
  },
});

const todayRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/planlayici",
  validateSearch: validateTodaySearch,
  component: lazy(() =>
    import("@/workspaces/planner/pages/TodayPage").then((m) => ({ default: m.TodayPage })),
  ),
});

const nativeWhatNowRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/simdi-ne",
  component: lazy(() =>
    import("@/workspaces/planner/pages/TodayPage").then((m) => ({
      default: m.NativeWhatNowPage,
    })),
  ),
});

const plannerCalendarAliasRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/planlayici/takvim",
  beforeLoad: () => {
    throw redirect({ to: "/planlayici", search: { gorunum: "hafta" } });
  },
});

const plansRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/planlayici/planlar",
  validateSearch: validateRootCollectionSearch,
  component: lazy(() =>
    import("@/modules/plans/public/plans-page").then((m) => ({ default: m.PlansPage })),
  ),
});

const planDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/plan/$planId",
  component: lazy(() =>
    import("@/modules/plans/public/plan-page").then((m) => ({
      default: m.PlanDetailPage,
    })),
  ),
});

const calendarShortcutRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/takvim",
  beforeLoad: () => {
    throw redirect({ to: "/planlayici", search: { gorunum: "hafta" } });
  },
});

const lifeLandingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/yasam",
  beforeLoad: () => {
    throw redirect({ to: "/yasam/takip" });
  },
});

const lifeTrackingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/yasam/takip",
  validateSearch: validateRecordSelectionSearch,
  component: lazy(() =>
    import("@/workspaces/life/pages/LifePages").then((m) => ({ default: m.LifeTrackingPage })),
  ),
});

const lifeInboxRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/yasam/bildirimler",
  component: lazy(() =>
    import("@/workspaces/life/pages/InboxPage").then((m) => ({ default: m.InboxPage })),
  ),
});

const notificationActionRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/yasam/bildirimler/eylem",
  validateSearch: validateNotificationActionSearch,
  component: lazy(() =>
    import("@/modules/notifications/public/routes").then((module) => ({
      default: module.NotificationActionReviewPage,
    })),
  ),
});

const lifeMetricsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/yasam/gostergeler",
  validateSearch: validateRecordSelectionSearch,
  component: lazy(() =>
    import("@/workspaces/life/pages/LifePages").then((m) => ({ default: m.LifeMetricsPage })),
  ),
});

const lifeRulesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/yasam/kurallar",
  validateSearch: validateRecordSelectionSearch,
  component: lazy(() =>
    import("@/workspaces/life/pages/LifePages").then((m) => ({ default: m.LifeRulesPage })),
  ),
});

const lifeAreasRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/yasam/alanlar",
  validateSearch: validateRecordSelectionSearch,
  component: lazy(() =>
    import("@/workspaces/life/pages/LifePages").then((m) => ({ default: m.LifeAreasPage })),
  ),
});

const lifeGuideRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/yasam/rehber",
  validateSearch: validateRecordSelectionSearch,
  component: lazy(() =>
    import("@/workspaces/life/pages/LifePages").then((m) => ({ default: m.LifeGuidePage })),
  ),
});

const LazyLifeAreaDetailPage = lazy(() =>
  import("@/workspaces/life/pages/LifePages").then((m) => ({ default: m.LifeAreaDetailPage })),
);
const LazyLifeSchemaPage = lazy(() =>
  import("@/workspaces/life/pages/LifePages").then((m) => ({ default: m.LifeSchemaPage })),
);
const LazyLifeItemDetailPage = lazy(() =>
  import("@/workspaces/life/pages/LifePages").then((m) => ({ default: m.LifeItemDetailPage })),
);

function LifeAreaDetailRoutePage() {
  const { areaId } = useParams({ from: "/app/yasam/alan/$areaId" });
  return <LazyLifeAreaDetailPage areaId={areaId} />;
}

function LifeSchemaRoutePage() {
  const { schemaId } = useParams({ from: "/app/yasam/tablo/$schemaId" });
  return <LazyLifeSchemaPage schemaId={schemaId} />;
}

function LifeItemDetailRoutePage() {
  const { itemId } = useParams({ from: "/app/yasam/kayit/$itemId" });
  return <LazyLifeItemDetailPage itemId={itemId} />;
}

const lifeAreaDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/yasam/alan/$areaId",
  component: LifeAreaDetailRoutePage,
});

const lifeSchemaRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/yasam/tablo/$schemaId",
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    ctx?: string;
    tab?: "metrics" | "rules" | "records";
    view?: "table" | "cards" | "timeline";
  } => ({
    ctx: typeof search.ctx === "string" ? search.ctx : undefined,
    tab:
      search.tab === "metrics" || search.tab === "rules" || search.tab === "records"
        ? search.tab
        : undefined,
    view:
      search.view === "table" || search.view === "cards" || search.view === "timeline"
        ? search.view
        : undefined,
  }),
  component: LifeSchemaRoutePage,
});

const lifeItemDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/yasam/kayit/$itemId",
  component: LifeItemDetailRoutePage,
});

const projectsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/calisma/projeler",
  validateSearch: validateProjectListSearch,
  component: lazy(() =>
    import("@/modules/projects/public/projects-page").then((m) => ({ default: m.ProjectsPage })),
  ),
});

const workdeskRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/calisma",
  validateSearch: validateWorkSearch,
  component: lazy(() =>
    import("@/workspaces/work/pages/WorkdeskPage").then((m) => ({ default: m.WorkdeskPage })),
  ),
});

const projectsShortcutRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/projeler",
  beforeLoad: () => {
    throw redirect({ to: "/calisma/projeler" });
  },
});

const tasksTabRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/planlayici/gorevler",
  validateSearch: validateTaskListSearch,
  component: lazy(() =>
    import("@/modules/tasks/public/tasks-page").then((m) => ({ default: m.TasksWorkspacePage })),
  ),
});

const routinesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/planlayici/rutinler",
  validateSearch: validateRootCollectionSearch,
  component: lazy(() =>
    import("@/modules/routines/public/routines-page").then((m) => ({ default: m.RoutinesPage })),
  ),
});

const routinesShortcutRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/rutinler",
  beforeLoad: () => {
    throw redirect({ to: "/planlayici/rutinler" });
  },
});

const notesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hafiza/notlar",
  validateSearch: validateNotesSearch,
  component: lazy(() =>
    import("@/workspaces/memory/pages/NotesPage").then((m) => ({ default: m.NotesPage })),
  ),
});

const memoryKnowledgeSpaceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hafiza/bilgi-alani",
  component: lazy(() =>
    import("@/workspaces/memory/pages/MemoryPage").then((m) => ({
      default: m.MemoryKnowledgeSpacePage,
    })),
  ),
});

const knowledgeItemsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hafiza/bilgiler",
  validateSearch: validateKnowledgeNetworkSearch,
  component: lazy(() =>
    import("@/workspaces/memory/pages/KnowledgePage").then((m) => ({
      default: m.KnowledgeNetworkPage,
    })),
  ),
});

const conceptMapsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hafiza/kavram-haritasi",
  validateSearch: validateKnowledgeNetworkSearch,
  component: lazy(() =>
    import("@/workspaces/memory/pages/KnowledgePage").then((m) => ({
      default: m.KnowledgeNetworkPage,
    })),
  ),
});

const notesShortcutRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/notlar",
  beforeLoad: () => {
    throw redirect({ to: "/hafiza/notlar" });
  },
});

const draftsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hafiza/taslaklar",
  validateSearch: (search: Record<string, unknown>): { taslak?: string; ctx?: string } => ({
    taslak: typeof search.taslak === "string" ? search.taslak : undefined,
    ctx: typeof search.ctx === "string" ? search.ctx : undefined,
  }),
  component: lazy(() =>
    import("@/workspaces/planner/pages/CapturePage").then((m) => ({ default: m.InboxPage })),
  ),
});

const nativeCaptureRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/yakala",
  component: lazy(() =>
    import("@/modules/capture/public/routes").then((module) => ({
      default: module.NativeCapturePage,
    })),
  ),
});

const triageRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hafiza/triage/$planId",
  component: lazy(() =>
    import("@/workspaces/memory/pages/TriagePage").then((m) => ({ default: m.TriagePage })),
  ),
});

const externalShareReviewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hafiza/paylasim",
  component: lazy(() =>
    import("@/modules/capture/public/routes").then((module) => ({
      default: module.ExternalShareReviewPage,
    })),
  ),
});

const declarativeImportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hafiza/ice-aktar",
  component: lazy(() =>
    import("@/modules/capture/public/routes").then((module) => ({
      default: module.DeclarativeImportPage,
    })),
  ),
});

const archiveRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/arsiv",
  component: lazy(() =>
    import("@/workspaces/planner/pages/LifecyclePages").then((m) => ({ default: m.ArchivePage })),
  ),
});

const trashRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/cop-kutusu",
  component: lazy(() =>
    import("@/workspaces/planner/pages/LifecyclePages").then((m) => ({ default: m.TrashPage })),
  ),
});

const projectRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/calisma/projeler/$projectId",
  validateSearch: validateProjectSearch,
  // `panel=plan` was the first routed form of the execution area. Plan and
  // Tasks is now the project's default, so converge old bookmarks without
  // dropping the context trail carried beside the retired parameter.
  beforeLoad: ({ params, search }) => {
    if (search.panel !== "plan") return;
    throw redirect({
      to: "/calisma/projeler/$projectId",
      params: { projectId: params.projectId },
      search: { ...search, panel: undefined },
      replace: true,
    });
  },
  component: lazy(() =>
    import("@/modules/projects/public/project-page").then((m) => ({ default: m.ProjectPage })),
  ),
});

// The product contract also publishes the short Turkish project URL. Keep the
// workspace-prefixed route as the internal canonical location, but make shared
// `/projeler/{id}` links lossless instead of falling through to the 404 page.
const shortProjectRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/projeler/$projectId",
  validateSearch: validateProjectSearch,
  beforeLoad: ({ params, search }) => {
    throw redirect({
      to: "/calisma/projeler/$projectId",
      params: { projectId: params.projectId },
      search: { ...search, panel: search.panel === "plan" ? undefined : search.panel },
      replace: true,
    });
  },
});

const taskRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/gorev/$taskId",
  component: lazy(() =>
    import("@/modules/tasks/public/task-page").then((m) => ({ default: m.TaskDetailPage })),
  ),
});

const routineRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/rutin/$routineId",
  component: lazy(() =>
    import("@/modules/routines/public/routine-page").then((m) => ({
      default: m.RoutineDetailPage,
    })),
  ),
});

const noteRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/not/$noteId",
  validateSearch: (search: Record<string, unknown>): { panel?: string } => ({
    panel: typeof search.panel === "string" ? search.panel : undefined,
  }),
  component: lazy(() =>
    import("@/workspaces/memory/pages/NotesPage").then((m) => m.loadNoteEditorPage()),
  ),
});

const analysisRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/analiz",
  validateSearch: validateAnalysisSearch,
  component: lazy(() =>
    import("@/workspaces/analysis/pages/AnalysisPage").then((m) => ({ default: m.AnalysisPage })),
  ),
});

const rediscoverRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/analiz/yeniden-kesfet",
  component: lazy(() =>
    import("@/workspaces/analysis/pages/ReviewPage").then((m) => ({ default: m.ReviewPage })),
  ),
});

const analysisHistoryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/analiz/gecmis",
  validateSearch: validateAnalysisSearch,
  component: lazy(() =>
    import("@/modules/analysis").then((m) => ({ default: m.AnalysisHistoryPage })),
  ),
});

const periodsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/planlayici/donemler",
  component: lazy(() =>
    import("@/modules/periods/public/periods-page").then((m) => ({ default: m.PeriodsPage })),
  ),
});

const periodDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/planlayici/donemler/$periodId",
  component: lazy(() =>
    import("@/modules/periods/public/period-page").then((m) => ({ default: m.PeriodDetailPage })),
  ),
});

const memoryLandingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hafiza",
  beforeLoad: () => {
    throw redirect({ to: "/hafiza/notlar" });
  },
});

const weeklyResetRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/planlayici/haftalik",
  component: lazy(() =>
    import("@/workspaces/analysis/pages/ReviewPage").then((m) => ({
      default: m.WeeklyResetPage,
    })),
  ),
});

const adaptationRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/planlayici/uyarlama",
  validateSearch: validateRecordSelectionSearch,
  component: lazy(() =>
    import("@/modules/adaptation/public/routes").then((m) => ({ default: m.AdaptationPage })),
  ),
});

const focusRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/calisma/odak",
  validateSearch: validateFocusSearch,
  component: lazy(() =>
    import("@/workspaces/work/pages/FocusPage").then((m) => ({ default: m.FocusPage })),
  ),
});

const focusShortcutRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/odak",
  validateSearch: validateFocusSearch,
  beforeLoad: ({ search }) => {
    throw redirect({ to: "/calisma/odak", search });
  },
});

const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/ayarlar",
  component: lazy(() =>
    import("@/modules/identity/public/settings-page").then((m) => ({ default: m.SettingsPage })),
  ),
});

const profileRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/ayarlar/profil",
  component: lazy(() =>
    import("@/modules/profile").then((module) => ({ default: module.ProfilePage })),
  ),
});

const assistantConnectionsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/ayarlar/bagli-asistanlar",
  component: lazy(() =>
    import("@/modules/identity/public/assistant-connections-page").then((m) => ({
      default: m.AssistantConnectionsPage,
    })),
  ),
});

const assistantMemoryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/ayarlar/asistan-hafizasi",
  component: lazy(() =>
    import("@/modules/assistant").then((m) => ({ default: m.AssistantMemoryPage })),
  ),
});

const assistantRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/asistan",
  component: lazy(() => import("@/modules/assistant").then((m) => ({ default: m.AssistantPage }))),
});

const onboardingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/baslangic",
  validateSearch: (search: Record<string, unknown>): { next?: string } => ({
    next:
      typeof search.next === "string" &&
      search.next.startsWith("/") &&
      !search.next.startsWith("//")
        ? search.next
        : undefined,
  }),
  component: lazy(() =>
    import("@/modules/identity/public/onboarding-page").then((module) => ({
      default: module.OnboardingPage,
    })),
  ),
});

const activitySecurityRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/ayarlar/etkinlik-guvenlik",
  component: lazy(() =>
    import("@/modules/activity").then((m) => ({ default: m.ActivitySecurityPage })),
  ),
});

const privacyRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/ayarlar/gizlilik",
  component: lazy(() =>
    import("@/modules/privacy").then((m) => ({ default: m.PrivacyControlPage })),
  ),
});

const extensionsCatalogRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/eklentiler",
  component: lazy(() =>
    import("@/workspaces/extensions/pages/ExtensionFeaturePages").then((m) => ({
      default: m.ExtensionsPage,
    })),
  ),
});

const extensionMusicRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/eklentiler/muzik",
  component: lazy(() =>
    import("@/workspaces/extensions/pages/ExtensionFeaturePages").then((m) => ({
      default: m.MusicPage,
    })),
  ),
});

const extensionLessonsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/eklentiler/dersler",
  component: lazy(() =>
    import("@/workspaces/extensions/pages/ExtensionFeaturePages").then((m) => ({
      default: m.LessonsPage,
    })),
  ),
});

const extensionCyclingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/eklentiler/bisiklet",
  component: lazy(() =>
    import("@/workspaces/extensions/pages/ExtensionFeaturePages").then((m) => ({
      default: m.CyclingPage,
    })),
  ),
});

const extensionDeviceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/eklentiler/cihazlar",
  component: lazy(() =>
    import("@/workspaces/extensions/pages/ExtensionFeaturePages").then((m) => ({
      default: m.DeviceTrackingPage,
    })),
  ),
});

const extensionSocialRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/eklentiler/sosyal",
  component: lazy(() =>
    import("@/workspaces/extensions/pages/ExtensionFeaturePages").then((m) => ({
      default: m.SocialPage,
    })),
  ),
});

const extensionPreferencesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/eklentiler/uygulama-tercihleri",
  component: lazy(() =>
    import("@/workspaces/extensions/pages/ExtensionFeaturePages").then((m) => ({
      default: m.ApplicationPreferencesPage,
    })),
  ),
});

const routeTree = rootRoute.addChildren([
  authRoute.addChildren([loginRoute, registerRoute, resetRoute, legacyResetRoute]),
  mobileGateRoute,
  appRoute.addChildren([
    pagesRoute,
    pageDocumentRoute,
    pageEntityDocumentRoute,
    indexRoute,
    globalSearchRoute,
    savedViewsRoute,
    tasksShortcutRoute,
    todayShortcutRoute,
    todayRoute,
    nativeWhatNowRoute,
    plannerCalendarAliasRoute,
    calendarShortcutRoute,
    planDetailRoute,
    plansRoute,
    lifeLandingRoute,
    lifeTrackingRoute,
    lifeAreasRoute,
    lifeInboxRoute,
    notificationActionRoute,
    lifeMetricsRoute,
    lifeRulesRoute,
    lifeGuideRoute,
    lifeAreaDetailRoute,
    lifeSchemaRoute,
    lifeItemDetailRoute,
    projectsRoute,
    workdeskRoute,
    projectsShortcutRoute,
    tasksTabRoute,
    routinesRoute,
    routinesShortcutRoute,
    notesRoute,
    memoryKnowledgeSpaceRoute,
    knowledgeItemsRoute,
    conceptMapsRoute,
    notesShortcutRoute,
    draftsRoute,
    nativeCaptureRoute,
    triageRoute,
    externalShareReviewRoute,
    declarativeImportRoute,
    archiveRoute,
    trashRoute,
    projectRoute,
    shortProjectRoute,
    taskRoute,
    routineRoute,
    noteRoute,
    analysisRoute,
    rediscoverRoute,
    analysisHistoryRoute,
    // The detail route is registered before the list so the parameterised
    // period path is matched by the more specific pattern.
    periodDetailRoute,
    periodsRoute,
    memoryLandingRoute,
    weeklyResetRoute,
    adaptationRoute,
    focusRoute,
    focusShortcutRoute,
    settingsRoute,
    profileRoute,
    assistantConnectionsRoute,
    assistantMemoryRoute,
    assistantRoute,
    onboardingRoute,
    activitySecurityRoute,
    privacyRoute,
    extensionsCatalogRoute,
    extensionMusicRoute,
    extensionLessonsRoute,
    extensionCyclingRoute,
    extensionDeviceRoute,
    extensionSocialRoute,
    extensionPreferencesRoute,
  ]),
]);

export function makeRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: "intent",
    defaultPreloadStaleTime: 0,
    scrollRestoration: true,
  });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof makeRouter>;
  }
}
